// src/pages/ViewDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiDownload,
  FiFileText,
  FiArrowLeft,
  FiX,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import { authHeader, handle401Error } from "../utils/auth";
import { API_BASE } from "../config";

/* ====================== MAIN PAGE: LICENSE DETAIL ====================== */
export default function CheckLicense() {
  const navigate = useNavigate();
  const blue = "#3366CC";
  const text = "#0F172A";
  const isLoggedIn =
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true";

  const container = {
    width: "100%",
    background: "#fff",
    overflow: "visible",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  };

  // ====== STATE ======
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null); // object ที่ map แล้วพร้อมแสดงผล
  const [orgInfo, setOrgInfo] = useState({ company: "", email: "" }); // จาก /api/me

  // ====== HELPERS ======
  // ใช้ authHeader จาก utils/auth.js

  const mapLicenseToDetail = (lic, me) => {
    // lic fields from backend
    // id, license_key, term, product_sku, duration_days, max_activations, activations_used
    // status, issued_at, expires_at
    const fmt = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "-");

    const remainDays = (() => {
      if (!lic.expires_at) return null;
      const end = new Date(lic.expires_at).setHours(0, 0, 0, 0);
      const now = new Date().setHours(0, 0, 0, 0);
      return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    })();

    const statusComputed =
      lic.expires_at && remainDays !== null && remainDays <= 0
        ? "Expired"
        : (lic.status || "active").toLowerCase() === "inactive"
        ? "Expired"
        : "Active";

    const typeLabel = (() => {
      const t = (lic.term || "").toLowerCase();
      if (t === "trial") return "Trial";
      if (t === "subscription") return "Subscription";
      if (t === "perpetual") return "Perpetual";
      return lic.term || "-";
    })();

    return {
      key: lic.license_key,
      status: statusComputed,
      issueDate: fmt(lic.issued_at),
      expiryDate: fmt(lic.expires_at),
      productName:
        lic.product_sku && lic.product_sku !== "-" ? lic.product_sku : "Smart Audit",
      devicesLinked:
        typeof lic.activations_used === "number" &&
        typeof lic.max_activations === "number"
          ? `${lic.activations_used}/${lic.max_activations} Devices`
          : "-",
      lastActivation: "-", // ถ้ายังไม่มีใน backend ใส่ "-" ไปก่อน
      activationStatus:
        statusComputed === "Active" ? "Not Activated on this device" : statusComputed,
      notes: "-", // ช่องอธิบายภายใน ถ้ายังไม่มีเก็บใน backend
      licenseSource: typeLabel === "Trial" ? "Trial" : "Direct Purchase",
      licenseType: typeLabel,
      client: me?.company || "-",
      email: me?.email || "-",
    };
  };

  // ====== LOAD DETAIL ======
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        // 1) ดึง me
        const meRes = await fetch(`${API_BASE}/me`, {
          headers: { "Content-Type": "application/json", ...authHeader() },
        });
        if (handle401Error(meRes, navigate)) {
          return;
        }
        if (!meRes.ok) {
          const err = await meRes.json().catch(() => ({}));
          throw new Error(err?.detail || "Failed to load user data.");
        }
        const me = await meRes.json();
        setOrgInfo({ company: me?.company || "", email: me?.email || "" });

        // 2) หาแหล่งข้อมูล license ที่ผู้ใช้เลือกจากหน้า List
        //    กรณี A: มี selectedLicenseKey ใน sessionStorage
        const selectedKey = sessionStorage.getItem("selectedLicenseKey");
        if (selectedKey) {
          // ใช้ key เพื่อค้นหา license จาก API
          const pendingKey = selectedKey.trim();
          sessionStorage.removeItem("selectedLicenseKey"); // ลบหลังจากใช้แล้ว
          
          // ดึง licenses จาก API และหา license ที่ตรงกับ key
          const licRes = await fetch(
            `${API_BASE}/licenses?clientId=${encodeURIComponent(me?.id)}`,
            {
              headers: { "Content-Type": "application/json", ...authHeader() },
            }
          );
          if (!licRes.ok) {
            const err = await licRes.json().catch(() => ({}));
            throw new Error(err?.detail || "Failed to load licenses.");
          }
          const raw = await licRes.json();
          const found = (raw || []).find(
            (x) => (x.license_key || x.licenseKey) === pendingKey
          );
          if (found) {
            const lic = {
              license_key: found.license_key || found.licenseKey,
              term: found.term || found.type,
              product_sku: found.product_sku || found.productSku,
              duration_days: found.duration_days || found.durationDays,
              max_activations: found.max_activations ?? found.maxActivations,
              activations_used: found.activations_used ?? found.activationsUsed,
              status: found.status,
              issued_at: found.issued_at || found.issuedAt,
              expires_at: found.expires_at || found.expiresAt,
            };
            setDetail(mapLicenseToDetail(lic, me));
            return;
          }
        }

        //    กรณี B: มีแค่ key ใน sessionStorage (pendingLicenseQuery)
        const pendingKey = (sessionStorage.getItem("pendingLicenseQuery") || "").trim();
        if (!pendingKey) {
          throw new Error("No item selected from License List.");
        }

        // 3) โหลด licenses ของ client นี้ แล้ว find ด้วย key
        const licRes = await fetch(
          `${API_BASE}/licenses?clientId=${encodeURIComponent(me?.id)}`,
          {
            headers: { "Content-Type": "application/json", ...authHeader() },
          }
        );
        if (!licRes.ok) {
          const err = await licRes.json().catch(() => ({}));
          throw new Error(err?.detail || "Failed to load licenses.");
        }
        const raw = await licRes.json();
        const found = (raw || []).find(
          (x) => (x.license_key || x.licenseKey) === pendingKey
        );
        if (!found) {
          throw new Error("License not found for the provided key.");
        }

        setDetail(
          mapLicenseToDetail(
            {
              license_key: found.license_key || found.licenseKey,
              term: found.term || found.type,
              product_sku: found.product_sku || found.productSku,
              duration_days: found.duration_days || found.durationDays,
              max_activations: found.max_activations ?? found.maxActivations,
              activations_used: found.activations_used ?? found.activationsUsed,
              status: found.status,
              issued_at: found.issued_at || found.issuedAt,
              expires_at: found.expires_at || found.expiresAt,
            },
            me
          )
        );
      } catch (e) {
        setError(e?.message || "An error occurred while loading license details.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remainDays = useMemo(() => {
    if (!detail?.expiryDate || detail.expiryDate === "-") return null;
    const end = new Date(detail.expiryDate).setHours(0, 0, 0, 0);
    const now = new Date().setHours(0, 0, 0, 0);
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  }, [detail]);

  const badgeStyle = (status) => {
    let color = "#111827",
      bg = "#E5E7EB";
    if (status === "Active") {
      color = "#16A34A";
      bg = "rgba(34,197,94,.12)";
    } else if (status === "Expired") {
      color = "#EF4444";
      bg = "rgba(239,68,68,.12)";
    } else if (status === "Suspended") {
      color = "#B45309";
      bg = "rgba(245,158,11,.14)";
    }
    return {
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: 999,
      fontWeight: 800,
      color,
      background: bg,
    };
  };

  const responsiveCSS = `
  * { box-sizing: border-box; }
  
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }

  .page-container {
    width: 100% !important;
    overflow-x: hidden;
    background: #fff;
  }
  
  .grid-2 { 
    display: grid !important;
    grid-template-columns: 1fr 1fr; /* Desktop */
    gap: 24px;
    padding: 24px;
    width: 100%;
  }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }

    .desktop-nav { 
      display: none !important; 
    }

    .hamburger-btn { 
      display: inline-flex !important; 
    }

    .sticky-nav {
      height: 60px !important;
      padding: 0 16px !important;
    }
    
    .grid-2 { 
      grid-template-columns: 1fr !important; 
      gap: 24px !important; 
    }

    .account-menu-panel {
      right: 10px !important;
      top: 50px !important;
      width: calc(100vw - 20px) !important;
      max-width: 300px !important;
    }

    .hero { 
      padding: 60px 20px !important; 
      min-height: auto !important;
    }

    .hero-title { 
      font-size: 32px !important; 
      text-align: center !important; 
      width: 100%;
    }

    .cta-buttons { 
      flex-direction: column !important; 
      width: 100% !important; 
      gap: 12px !important;
    }

    .cta-buttons > button { 
      width: 100% !important; 
      margin: 0 !important;
    }

    .what-is, #why-smartclick, #cta-card, #free-trial + section {
      padding: 40px 20px !important;
    }
    
    .grid-2 { 
      grid-template-columns: 1fr !important; 
      padding: 16px !important;
      gap: 16px !important; 
    }

    .card-header-flex {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px;
    }

    .action-bar-flex {
      flex-direction: column !important;
      width: 100%;
    }
    
    .action-bar-flex > a, .action-bar-flex > button {
      width: 100% !important;
      justify-content: center;
    }

    .license-key-text {
      font-size: 16px !important;
      word-break: break-all; /* ป้องกัน Key ยาวเกินหน้าจอ */
    }
  }

  [id] { scroll-margin-top: 70px; }
`;

  return (
    <>
      <style>{responsiveCSS}</style>
      <div style={container}>
        {/* Sticky header */}
        <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
          <TopInfoBar blue={blue} />
          <Navbar text={text} isLoggedIn={isLoggedIn} />
        </div>

        {/* Hero */}
        <section
          style={{ background: "#0E2B55", color: "#fff", padding: "28px 24px 30px" }}
        >
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <h1
              style={{
                margin: 0,
                fontSize: 36,
                fontWeight: 800,
                letterSpacing: 0.2,
              }}
            >
              LICENSE DETAIL
            </h1>
            {!!orgInfo.company && (
              <p style={{ margin: "8px 0 0", color: "#93c5fd" }}>
                Organization: <b>{orgInfo.company}</b>
              </p>
            )}
            <p
              style={{
                margin: "10px 0 0",
                color: "#2BB0F6",
                opacity: 0.95,
                lineHeight: 1.55,
              }}
            >
              Review license information, validity, and usage for your account.
            </p>
          </div>
        </section>

        {/* Content */}
        <section style={{ background: "#F3F6FB", padding: "42px 16px 80px" }}>
          <div
            style={{
              maxWidth: 1120,
              margin: "0 auto",
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: 16,
            }}
          >
            {/* Header of card + back */}
            <div className="card-header-flex"
              style={{
                padding: 20,
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 13, color: "#64748B" }}>License Key</div>
                <div style={{ fontWeight: 800, fontSize: 18, marginTop: 4 }}>
                  {detail?.key || (loading ? "Loading..." : "—")}
                </div>
              </div>
              <button 
                onClick={() => navigate("/List")} 
                aria-label="Go back to license list"
                style={actionBtn("ghost")}
              >
                <><FiArrowLeft size={18} style={{ marginRight: 8, flexShrink: 0 }} /> Back to License List</>
              </button>
            </div>

            {loading ? (
              <div 
                role="status"
                aria-live="polite"
                aria-label="Loading license details"
                style={{ padding: 40, textAlign: "center", color: "#64748B" }}
              >
                Loading...
              </div>
            ) : error ? (
              <div style={{ padding: 24 }}>
                <div
                  role="alert"
                  aria-live="polite"
                  style={{
                    marginBottom: 16,
                    borderRadius: 10,
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    padding: "12px 16px",
                    color: "#b91c1c",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FiX size={16} /> {error}
                </div>
                <div>
                  <button
                    onClick={() => navigate("/List")}
                    aria-label="Go back to license list"
                    style={actionBtn("primary")}
                  >
                    Go to License List
                  </button>
                </div>
              </div>
            ) : !detail ? (
              <div style={{ padding: 24 }}>
                <div 
                  role="status"
                  style={{
                    marginBottom: 16,
                    fontWeight: 800,
                    fontSize: 18,
                    color: "#475569",
                  }}
                >
                  License not found
                </div>
                <p style={{ color: "#475569", marginBottom: 16 }}>
                  We couldn't find the license you selected. Please go back to the
                  License List and select an item again.
                </p>
                <div>
                  <button
                    onClick={() => navigate("/List")}
                    aria-label="Go back to license list"
                    style={actionBtn("primary")}
                  >
                    Go to License List
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Two-column details */}
                <div
                  className="grid-2"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 24,
                    padding: 24,
                  }}
                >
                  {/* Left */}
                  <div className="grid-2" style={cardBox}>
                    <Row
                      label="License Status"
                      value={
                        <span style={badgeStyle(detail.status)}>{detail.status}</span>
                      }
                    />
                    <Row label="Issue Date" value={fmtDate(detail.issueDate)} />
                    <Row label="Expiration Date" value={fmtDate(detail.expiryDate)} />
                    <Row
                      label="Remaining Days"
                      value={
                        remainDays == null
                          ? "-"
                          : remainDays > 0
                          ? `${remainDays} days remaining`
                          : "Expired"
                      }
                    />
                    <Row label="Product Name" value={detail.productName} />
                    <Row label="Devices Linked" value={detail.devicesLinked} />
                    <Row
                      label="Last Activation Date"
                      value={fmtDate(detail.lastActivation)}
                    />
                  </div>

                  {/* Right */}
                  <div style={cardBox}>
                    <Row label="Activation Status" value={detail.activationStatus} />
                    <Row label="Notes / Internal Ref." value={detail.notes} />
                    <Row label="License Source" value={detail.licenseSource} />
                    <Row label="License Type" value={detail.licenseType} />
                    <Row
                      label="Client / Organization"
                      value={detail.client || orgInfo.company || "-"}
                    />
                    <Row
                      label="Registered Email"
                      value={
                        <a
                          href={`mailto:${detail.email || orgInfo.email || ""}`}
                          style={{ color: "#0EA5E9", textDecoration: "underline" }}
                        >
                          {detail.email || orgInfo.email || "-"}
                        </a>
                      }
                    />
                  </div>
                </div>

                {/* Action bar */}
                <div className="action-bar-flex"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 24px 24px",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <a href="/license-sample.pdf" download style={actionBtn()}>
                      <FiDownload /> Download PDF
                    </a>
                    <a href="/terms" style={actionBtn()}>
                      <FiFileText /> View Terms
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

/* ========= Inline components removed - using shared components ========= */
function Row({ label, value }) {
  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      padding: "10px 0", 
      borderBottom: "1px inset #f1f5f9",
      gap: "10px" // เพิ่ม gap ป้องกัน label กับ value ชนกัน
    }}>
      <span style={{ color: "#64748B", fontSize: 13, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 13, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso || iso === "-") return "-";
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return iso;
  }
}

function actionBtn(variant) {
  const base = {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: "1px solid #CBD5E1",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "none",
  };
  if (variant === "primary") {
    return {
      ...base,
      background: "#0C67B1",
      color: "#fff",
      border: "1px solid #0B5C9E",
    };
  }
  if (variant === "ghost") {
    return {
      ...base,
      background: "transparent",
      color: "#2563EB",
      border: "none",
      padding: 0,
    };
  }
  return {
    ...base,
    background: "#E6F2FB",
    color: "#0B66A7",
    border: "1px solid #CFE8FA",
  };
}

const cardBox = {
  border: "1px solid #E5E7EB",
  borderRadius: 12,
  padding: 16,
};
